/** Staff Groups (RBAC) — список + permission-matrix editor. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Shield, Trash2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface Permission {
  id: number;
  code: string;
  description: string;
}

interface Group {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: Permission[];
  member_count: number;
  created_at: string;
}

export function StaffGroupsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ["staff-groups"],
    queryFn: async () => {
      const { data } = await api.get("/staff/groups");
      return data as Group[];
    },
  });

  const { data: allPermissions } = useQuery({
    queryKey: ["staff-permissions"],
    queryFn: async () => {
      const { data } = await api.get("/staff/permissions");
      return data as Permission[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/staff/groups/${id}`);
    },
    onSuccess: () => {
      toast.success("Группа удалена");
      queryClient.invalidateQueries({ queryKey: ["staff-groups"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось удалить"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить группу?",
  });

  if (groupsLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Группы прав (RBAC)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Назначаются на staff-аккаунты. Системные группы нельзя удалить.
          </p>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать группу
        </Button>
      </div>

      {editingId === "new" && allPermissions && (
        <GroupForm
          mode="create"
          allPermissions={allPermissions}
          onClose={() => setEditingId(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {groups?.map((g) => (
          <Card key={g.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {g.name}
                    {g.is_system && (
                      <Badge variant="secondary" className="text-[10px]">
                        system
                      </Badge>
                    )}
                  </CardTitle>
                  {g.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {g.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <Users className="w-3.5 h-3.5" />
                  {g.member_count}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {g.permissions.length === 0 ? (
                  <span className="text-xs text-muted-foreground italic">
                    Нет permissions
                  </span>
                ) : (
                  g.permissions.map((p) => (
                    <Badge
                      key={p.id}
                      variant="outline"
                      className="text-[10px] font-mono"
                    >
                      {p.code}
                    </Badge>
                  ))
                )}
              </div>

              {editingId === g.id && allPermissions ? (
                <GroupForm
                  mode="edit"
                  group={g}
                  allPermissions={allPermissions}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(g.id)}
                  >
                    Редактировать
                  </Button>
                  {!g.is_system && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => askDelete(g.id, g.name)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function GroupForm({
  mode,
  group,
  allPermissions,
  onClose,
}: {
  mode: "create" | "edit";
  group?: Group;
  allPermissions: Permission[];
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(group?.name ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(
    new Set(group?.permissions.map((p) => p.code) ?? []),
  );

  useEffect(() => {
    setName(group?.name ?? "");
    setDescription(group?.description ?? "");
    setSelectedCodes(new Set(group?.permissions.map((p) => p.code) ?? []));
  }, [group]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        permission_codes: Array.from(selectedCodes),
      };
      if (mode === "create") {
        await api.post("/staff/groups", payload);
      } else if (group) {
        await api.patch(`/staff/groups/${group.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Группа создана" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["staff-groups"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  function toggle(code: string) {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <Card className={mode === "edit" ? "mt-3 border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новая группа</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4 pt-4">
        <div className="space-y-1.5">
          <Label>Название *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Менеджер каталога"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Описание</Label>
          <Textarea
            value={description ?? ""}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div>
          <Label className="mb-2 block">
            Permissions ({selectedCodes.size} / {allPermissions.length})
          </Label>
          <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
            {allPermissions.map((p) => (
              <label
                key={p.id}
                className="flex items-start gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer"
              >
                <Checkbox
                  checked={selectedCodes.has(p.code)}
                  onChange={() => toggle(p.code)}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-mono">{p.code}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!name.trim() || saveMutation.isPending}
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
